/**
 * Shared PT goal row helpers — Progress Toward Goals (reeval) and
 * Short/Long-term text goals (eval / visit). Supports +Add Goal / remove.
 */

export const newGoalId = (prefix = "goal") =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

/** Progress-toward-goals row (Re-evaluation Note table). */
export const emptyProgressGoalRow = (n) => ({
  id: newGoalId(`goal-${n}`),
  label: `Goal ${n}`,
  goalText: "",
  status: "",
  evidence: "",
  continueGoal: "",
});

export const emptyProgressGoals = (count = 3) =>
  Array.from({ length: Math.max(1, count) }, (_, i) =>
    emptyProgressGoalRow(i + 1),
  );

export const renumberProgressGoals = (rows) =>
  (Array.isArray(rows) ? rows : []).map((row, idx) => ({
    id: row?.id || newGoalId(`goal-${idx + 1}`),
    label: `Goal ${idx + 1}`,
    goalText: row?.goalText || "",
    status: row?.status || "",
    evidence: row?.evidence || "",
    continueGoal: row?.continueGoal || "",
  }));

/**
 * Normalize progress goals from payload. Keeps all saved rows; seeds defaults
 * when empty so the form always shows at least `minCount` editable rows.
 */
export const normalizeProgressGoals = (rows, minCount = 3) => {
  const goals = Array.isArray(rows) ? rows : [];
  if (!goals.length) return emptyProgressGoals(minCount);
  return renumberProgressGoals(goals);
};

/** Free-text goal row (Eval / Visit ST & LT lists). */
export const emptyTextGoalRow = (n, kind = "st") => ({
  id: newGoalId(`${kind}-${n}`),
  text: "",
});

export const emptyTextGoals = (count = 1, kind = "st") =>
  Array.from({ length: Math.max(1, count) }, (_, i) =>
    emptyTextGoalRow(i + 1, kind),
  );

export const renumberTextGoals = (rows, kind = "st") =>
  (Array.isArray(rows) ? rows : []).map((row, idx) => ({
    id: row?.id || newGoalId(`${kind}-${idx + 1}`),
    text: typeof row === "string" ? row : row?.text || "",
  }));

/**
 * Prefer `shortTermGoals` / `longTermGoals` arrays; fall back to legacy
 * `shortTermGoal1`…`shortTermGoalN` fields from older payloads.
 */
export const normalizeTextGoals = (payload, { arrayKey, legacyPrefix, kind, minCount = 1 }) => {
  const fromArray = Array.isArray(payload?.[arrayKey]) ? payload[arrayKey] : null;
  if (fromArray && fromArray.length) {
    return renumberTextGoals(fromArray, kind);
  }

  const legacy = [];
  for (let n = 1; n <= 12; n += 1) {
    const key = `${legacyPrefix}${n}`;
    if (payload && Object.prototype.hasOwnProperty.call(payload, key)) {
      const text = String(payload[key] || "");
      if (text.trim() || n <= minCount) {
        legacy.push({ id: newGoalId(`${kind}-${n}`), text });
      }
    }
  }
  if (legacy.length) return renumberTextGoals(legacy, kind);
  return emptyTextGoals(minCount, kind);
};

export const serializeTextGoals = (rows) =>
  renumberTextGoals(rows).map((row) => ({
    id: row.id,
    text: String(row.text || "").trim(),
  }));
